import React, { useState } from "react";
//#region types
import { BankIdSignDialogContentType, DialogProps } from "models/propTypes/dialogPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Dialog, Grid } from "@mui/material";
import useFloatingMessages from "../messages/useFloatingMessages";
import BankIdLoading from "../bankId/loading";
import BankIdQRCode from "../bankId/qrCode";
import { usePaymentService } from "serviceHooks";
//#endregion
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";
import StyledButton from "../buttons/styledButton";

const BankIdSignDialog: React.FC<DialogProps> = (props) => {
  const paymentService = usePaymentService();
  const { getSignKeyHandler, successMessage } = props.children as BankIdSignDialogContentType;
  const { enqueueMessage } = useFloatingMessages();
  const [autostartToken, setAutostartToken] = useState<string>();
  const [qrCode, setQrCode] = useState<string>();

  const onClose = () => {
    props.onClose && props.onClose();
    props.closeDialog && props.closeDialog();
  };

  const updateQrCode = (qrStartCode: string, time: string, qrAuthCode: string) => {
    setQrCode(`bankid.${qrStartCode}.${time}.${qrAuthCode}`);
  };

  const checkBankIdStatus = async (bankId: number) => {
    const res = await paymentService.getBankIdStatus(bankId);
    if (res.state === WintSuperkollAPIModel.BankIdSignState.Signed) {
      successMessage &&
        enqueueMessage(LocalizationHelperInstance.getFormattedMessage(successMessage), StyleEnums.DisplayType.Success);
      props.onSubmit && props.onSubmit(res);
      props.closeDialog && props.closeDialog();
    } else if (res.state === WintSuperkollAPIModel.BankIdSignState.Waiting) {
      res.bankIdData && updateQrCode(res.bankIdData.qRStartToken!, res.bankIdData.time!, res.bankIdData.qRAuthCode!);
      setTimeout(async () => {
        checkBankIdStatus(bankId);
      }, 500);
    } else {
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(
          res.state === WintSuperkollAPIModel.BankIdSignState.Timeout
            ? TranslationKeys.Payments.BankIdSignTimeout
            : TranslationKeys.Payments.BankIdSignError
        ),
        StyleEnums.DisplayType.Danger,
        true
      );
      props.onError && props.onError(res, props.closeDialog);
      props.closeDialog && props.closeDialog();
    }
  };

  React.useEffect(() => {
    getSignKeyHandler()
      .then((res) => {
        if (res.bankIdData) {
          updateQrCode(res.bankIdData!.qRStartToken!, res.bankIdData!.time!, res.bankIdData!.qRAuthCode!);
        }
        setAutostartToken(res.bankIdData?.autoStartToken);
        checkBankIdStatus(res.id);
      })
      .catch((error) => {
        onClose();
      });
  }, []);

  const openBankId = React.useCallback(() => {
    if (!autostartToken) {
      return;
    }
    window.open(`bankid:///?autostarttoken=${autostartToken}&redirect=null`, "_self");
  }, [autostartToken]);

  // Problem with opening BankID on desktop, so that button will be hidden for non-mobile devices.
  // Detecting if touchscreen should be "good enough".
  const isTouchDevice = "ontouchstart" in window;

  const cancel = () => {
    onClose();
  };

  return (
    <Dialog
      aria-labelledby="dialog-title"
      open={props.isOpen}
      maxWidth="sm"
      hideBackdrop={false}
      classes={{
        root: cssExports.dialogRoot,
        paper: clsx(cssExports.simpleDialogRoot, props.className),
      }}
    >
      <Grid container justifyContent="center" alignItems="center">
        <Grid container className={cssExports.bankIdSign}>
          {qrCode ? <BankIdQRCode qrCodeData={qrCode} /> : <BankIdLoading></BankIdLoading>}
          <Grid item container>
            {autostartToken && isTouchDevice ? (
              <StyledButton onClick={openBankId} className={cssExports.loginButton} secondary>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Login.OpenBankId)}
              </StyledButton>
            ) : (
              <></>
            )}
            <StyledButton onClick={cancel} className={cssExports.cancelButton} secondary>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Cancel)}
            </StyledButton>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default BankIdSignDialog;
